/**
 * <MainButton> — drives Telegram WebApp.MainButton (when in Telegram) and
 * renders a sticky fallback button when outside Telegram (e.g. plain browser).
 */
import { useEffect } from 'react';
import { getWebApp, isInsideTelegram } from './telegram';
import { useT } from './i18n';
import { cn } from './cn';

// Waylo brand colors — passed to Telegram's MainButton so the primary action
// is always brand green, NOT the user's Telegram theme button color (blue).
// Telegram's setParams API requires literal hex strings, so these mirror
// packages/ui/src/tokens.css (--brand-green / --destructive).
const BRAND_GREEN = '#67b36b';
const BRAND_GREEN_TEXT = '#ffffff';
const DESTRUCTIVE = '#d24545';

interface Props {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'destructive';
}

export function MainButton({ text, onClick, disabled, loading, variant = 'primary' }: Props): JSX.Element | null {
  const inTelegram = isInsideTelegram();
  const { t } = useT();

  useEffect(() => {
    if (!inTelegram) return;
    const tg = getWebApp();
    if (!tg) return;
    tg.MainButton.setText(text);
    // Force brand green (or red for destructive) over the Telegram theme color.
    tg.MainButton.setParams?.({
      color: variant === 'destructive' ? DESTRUCTIVE : BRAND_GREEN,
      text_color: BRAND_GREEN_TEXT,
    });
    if (disabled) tg.MainButton.disable();
    else tg.MainButton.enable();
    if (loading) tg.MainButton.showProgress(true);
    else tg.MainButton.hideProgress();
    tg.MainButton.show();
    tg.MainButton.onClick(onClick);
    return () => {
      tg.MainButton.offClick(onClick);
      tg.MainButton.hide();
    };
  }, [text, disabled, loading, onClick, inTelegram, variant]);

  if (inTelegram) return null;

  return (
    <div className="sticky bottom-0 left-0 right-0 z-50 bg-tg-bg pb-[env(safe-area-inset-bottom)] pt-3">
      <button
        type="button"
        disabled={disabled || loading}
        onClick={onClick}
        className={cn(
          'w-full rounded-pill py-3.5 text-base font-semibold transition disabled:cursor-not-allowed disabled:opacity-50',
          variant === 'destructive'
            ? 'bg-destructive text-white'
            : 'bg-brand-green text-white hover:opacity-90',
        )}
      >
        {loading ? t('common.working') : text}
      </button>
    </div>
  );
}
